/**
 * PhoneInput — reusable phone field with a country-code dropdown.
 *
 * Why this exists:
 *  1. Every form on the site used a bare `<input type="tel">` with no
 *     autocomplete attribute, so Chrome's email-autofill heuristic
 *     would happily stuff someone's email address into the phone box.
 *     The explicit `autoComplete="tel-national"` + `name="phone"` here
 *     tells the browser exactly what this is.
 *  2. Saudi numbers can be entered either as +966501234567 or 0501234567.
 *     Storing the assembled E.164 string (+966...) makes WhatsApp links
 *     and tel: links work reliably.
 *
 * The component is a controlled input: parent owns the full assembled
 * value (`+966501234567`). On mount, we sniff the country code off the
 * string so the dropdown shows the right flag.
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Country {
  code: string;       // ISO-2 (for the React key + lookups)
  dial: string;       // dial code without the +, e.g. '966'
  flag: string;       // emoji flag
  nameEn: string;
  nameAr: string;
}

// Saudi + GCC first, then nearby region, then a useful long tail.
// Add to this list rather than swapping libraries when you need a new code.
const COUNTRIES: Country[] = [
  { code: 'SA', dial: '966', flag: '🇸🇦', nameEn: 'Saudi Arabia', nameAr: 'السعودية' },
  { code: 'AE', dial: '971', flag: '🇦🇪', nameEn: 'United Arab Emirates', nameAr: 'الإمارات' },
  { code: 'KW', dial: '965', flag: '🇰🇼', nameEn: 'Kuwait', nameAr: 'الكويت' },
  { code: 'QA', dial: '974', flag: '🇶🇦', nameEn: 'Qatar', nameAr: 'قطر' },
  { code: 'BH', dial: '973', flag: '🇧🇭', nameEn: 'Bahrain', nameAr: 'البحرين' },
  { code: 'OM', dial: '968', flag: '🇴🇲', nameEn: 'Oman', nameAr: 'عُمان' },
  { code: 'YE', dial: '967', flag: '🇾🇪', nameEn: 'Yemen', nameAr: 'اليمن' },
  { code: 'EG', dial: '20',  flag: '🇪🇬', nameEn: 'Egypt', nameAr: 'مصر' },
  { code: 'JO', dial: '962', flag: '🇯🇴', nameEn: 'Jordan', nameAr: 'الأردن' },
  { code: 'LB', dial: '961', flag: '🇱🇧', nameEn: 'Lebanon', nameAr: 'لبنان' },
  { code: 'SY', dial: '963', flag: '🇸🇾', nameEn: 'Syria', nameAr: 'سوريا' },
  { code: 'IQ', dial: '964', flag: '🇮🇶', nameEn: 'Iraq', nameAr: 'العراق' },
  { code: 'PS', dial: '970', flag: '🇵🇸', nameEn: 'Palestine', nameAr: 'فلسطين' },
  { code: 'SD', dial: '249', flag: '🇸🇩', nameEn: 'Sudan', nameAr: 'السودان' },
  { code: 'MA', dial: '212', flag: '🇲🇦', nameEn: 'Morocco', nameAr: 'المغرب' },
  { code: 'DZ', dial: '213', flag: '🇩🇿', nameEn: 'Algeria', nameAr: 'الجزائر' },
  { code: 'TN', dial: '216', flag: '🇹🇳', nameEn: 'Tunisia', nameAr: 'تونس' },
  { code: 'LY', dial: '218', flag: '🇱🇾', nameEn: 'Libya', nameAr: 'ليبيا' },
  { code: 'TR', dial: '90',  flag: '🇹🇷', nameEn: 'Turkey', nameAr: 'تركيا' },
  { code: 'PK', dial: '92',  flag: '🇵🇰', nameEn: 'Pakistan', nameAr: 'باكستان' },
  { code: 'IN', dial: '91',  flag: '🇮🇳', nameEn: 'India', nameAr: 'الهند' },
  { code: 'BD', dial: '880', flag: '🇧🇩', nameEn: 'Bangladesh', nameAr: 'بنغلاديش' },
  { code: 'PH', dial: '63',  flag: '🇵🇭', nameEn: 'Philippines', nameAr: 'الفلبين' },
  { code: 'ID', dial: '62',  flag: '🇮🇩', nameEn: 'Indonesia', nameAr: 'إندونيسيا' },
  { code: 'MY', dial: '60',  flag: '🇲🇾', nameEn: 'Malaysia', nameAr: 'ماليزيا' },
  { code: 'GB', dial: '44',  flag: '🇬🇧', nameEn: 'United Kingdom', nameAr: 'المملكة المتحدة' },
  { code: 'US', dial: '1',   flag: '🇺🇸', nameEn: 'United States', nameAr: 'الولايات المتحدة' },
  { code: 'CA', dial: '1',   flag: '🇨🇦', nameEn: 'Canada', nameAr: 'كندا' },
  { code: 'DE', dial: '49',  flag: '🇩🇪', nameEn: 'Germany', nameAr: 'ألمانيا' },
  { code: 'FR', dial: '33',  flag: '🇫🇷', nameEn: 'France', nameAr: 'فرنسا' },
];

const DEFAULT_COUNTRY = COUNTRIES[0]; // Saudi Arabia

/**
 * Sniff which country a stored phone string belongs to.
 * Accepts: "+966501234567", "966501234567", or "0501234567" (falls back to Saudi).
 * Returns the country + the local part (without the dial code).
 */
function splitPhone(value: string): { country: Country; local: string } {
  const trimmed = (value || '').trim();
  if (!trimmed) return { country: DEFAULT_COUNTRY, local: '' };

  const digitsOnly = trimmed.replace(/[^\d+]/g, '');
  const stripPlus = digitsOnly.startsWith('+') ? digitsOnly.slice(1) : digitsOnly;

  // Try the longest dial codes first so '+1' doesn't shadow '+1xxx'-style ones.
  const byLength = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of byLength) {
    if (stripPlus.startsWith(c.dial)) {
      return { country: c, local: stripPlus.slice(c.dial.length) };
    }
  }

  // No match → assume Saudi local number; drop a leading 0 if present.
  const local = stripPlus.startsWith('0') ? stripPlus.slice(1) : stripPlus;
  return { country: DEFAULT_COUNTRY, local };
}

function assemble(country: Country, local: string): string {
  const digits = (local || '').replace(/\D/g, '');
  if (!digits) return '';
  return `+${country.dial}${digits}`;
}

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional name passed to the underlying input — defaults to 'phone'. */
  name?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Visual error state; when truthy the border turns red. */
  hasError?: boolean;
  className?: string;
  onBlur?: () => void;
}

export default function PhoneInput({
  value,
  onChange,
  name = 'phone',
  id,
  placeholder,
  required = false,
  disabled = false,
  hasError = false,
  className = '',
  onBlur,
}: PhoneInputProps) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Derive the initial country + local part from the controlled `value`.
  const initial = useMemo(() => splitPhone(value), [value]);
  const [country, setCountry] = useState<Country>(initial.country);
  const [local, setLocal] = useState<string>(initial.local);
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // If the parent swaps `value` out from under us (e.g. form reset), resync.
  // We only sync when the assembled stored value diverges — otherwise typing
  // would fight with itself on every render.
  useEffect(() => {
    const currentAssembled = assemble(country, local);
    if (value !== currentAssembled) {
      const parsed = splitPhone(value);
      setCountry(parsed.country);
      setLocal(parsed.local);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close the country picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pickerOpen]);

  const handleLocalChange = (raw: string) => {
    // Strip everything but digits — country code lives in `country`, so the
    // user shouldn't be retyping it into the local box.
    const digits = raw.replace(/\D/g, '');
    setLocal(digits);
    onChange(assemble(country, digits));
  };

  const handleCountrySelect = (c: Country) => {
    setCountry(c);
    setPickerOpen(false);
    onChange(assemble(c, local));
  };

  const borderClass = hasError
    ? 'border-red-400 focus-within:ring-red-200'
    : 'border-gray-300 dark:border-gray-600 focus-within:ring-[#004D40]/30 focus-within:border-[#004D40]';

  return (
    <div
      ref={wrapperRef}
      className={`relative flex items-stretch rounded-lg border bg-white dark:bg-gray-900 transition focus-within:ring-2 ${borderClass} ${disabled ? 'opacity-60' : ''} ${className}`}
      // Force LTR so the country code reads correctly even on Arabic pages.
      dir="ltr"
    >
      {/* Country code dropdown trigger */}
      <button
        type="button"
        onClick={() => !disabled && setPickerOpen((open) => !open)}
        disabled={disabled}
        className="flex items-center gap-1.5 px-3 py-2.5 border-r border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition focus:outline-none"
        aria-haspopup="listbox"
        aria-expanded={pickerOpen}
        aria-label={isRTL ? 'اختر رمز الدولة' : 'Select country code'}
      >
        <span className="text-lg leading-none">{country.flag}</span>
        <span className="font-medium text-gray-700 dark:text-gray-200">+{country.dial}</span>
        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Number input */}
      <input
        type="tel"
        // Tell the browser this is a phone field (national part — country code
        // lives in the dropdown). This is the specific autocomplete value that
        // stops Chrome from suggesting email addresses for this field.
        autoComplete="tel-national"
        inputMode="tel"
        name={name}
        id={id}
        value={local}
        onChange={(e) => handleLocalChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder || (isRTL ? '5X XXX XXXX' : '5X XXX XXXX')}
        required={required}
        disabled={disabled}
        className="flex-1 min-w-0 px-3 py-2.5 bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
      />

      {/* Country picker dropdown */}
      {pickerOpen && (
        <ul
          role="listbox"
          aria-label={isRTL ? 'الدول' : 'Countries'}
          className="absolute top-full left-0 mt-1 z-30 w-72 max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"
        >
          {COUNTRIES.map((c) => (
            <li key={c.code}>
              <button
                type="button"
                role="option"
                aria-selected={c.code === country.code}
                onClick={() => handleCountrySelect(c)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition ${c.code === country.code ? 'bg-[var(--pv-color-surface-sunken)] dark:bg-violet-900/20' : ''}`}
              >
                <span className="text-lg leading-none">{c.flag}</span>
                <span className="flex-1 text-gray-800 dark:text-gray-200">
                  {isRTL ? c.nameAr : c.nameEn}
                </span>
                <span className="text-gray-500 dark:text-gray-400 font-medium">+{c.dial}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
